import { type ComponentPropsWithoutRef } from 'react'
import { type IETFLanguageTag } from '@opallabs/concepts'
import { type ExportPartsProp } from '@opallabs/gem'
import { type parts } from './styleHooks'

export type AvatarProps = Readonly<
  Pick<ComponentPropsWithoutRef<'img'>, 'aria-hidden' | 'className' | 'src'> & {
    /**
     * Overrides `fullName` in tooltip.
     *
     * @note You still need to explicitly set `tooltipped` to `true` if you provide `customTooltipText`.
     */
    customTooltipText?: string
    exportparts?: ExportPartsProp<typeof parts>
    /**
     * - If `src` loads, used as `alt` text; otherwise used for initials
     * - Default tooltip text when `tooltipped` is `true`
     */
    fullName: string
    /**
     * Maximum number of initials to show when `src` is falsy or doesn’t load.
     *
     * @default 2
     */
    initialsLength?: number
    /**
     * Affects how initials are extracted from `fullName`
     */
    locale?: IETFLanguageTag
    /** Selects the method used to transform `fullName` to initials:
     *  - 'extract' (default): Extracts initials using a segmenter algorithm.
     *  - 'truncate': Uses the first {initialsLength} characters from the string.
     *
     * @default "extract"
     */
    mode?: 'truncate' | 'extract'
    part?: string
    /**
     * Physical size of the avatar in pixels
     *
     * @default 20
     */
    size?: number
    /**
     * Show a tooltip with `customTooltipText` or `fullName`
     *
     * @default false
     */
    tooltipped?: boolean
  }
>

/** `Spacing.XS * 5`, written as a literal so that `styles.css` can read it with a pragma */
export const DEFAULT_SIZE = 20

/** For use in Storybook, which can’t extract destructured defaults within `forwardRef()` */
export const propDefaults = {
  initialsLength: 2,
  mode: 'extract',
  size: DEFAULT_SIZE,
  tooltipped: false,
} as const satisfies Omit<AvatarProps, 'fullName'>
